/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

const BUCKET_COLORS = {
    lt1h: { bg: '#dcfce7', fill: '#10b981', label: 'Good' },
    '1to4h': { bg: '#dbeafe', fill: '#3b82f6', label: 'Acceptable' },
    '4to24h': { bg: '#fef3c7', fill: '#f59e0b', label: 'Needs Improvement' },
    gt24h: { bg: '#fee2e2', fill: '#ef4444', label: 'Poor' },
};

const ResponseTimeChart = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.count, 0);
    const maxCount = Math.max(...data.map((item) => item.count), 1);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
            }}
        >
            {/* Header */}
            <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 3 }}>
                Response Time Distribution
            </Typography>

            {/* Chart */}
            {total === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                        No response data available
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {data.map((item) => {
                        const colorConfig = BUCKET_COLORS[item.bucket] || BUCKET_COLORS.lt1h;
                        const percentage = total > 0 ? (item.count / total) * 100 : 0;
                        const barWidth = (item.count / maxCount) * 100;

                        return (
                            <Box key={item.bucket}>
                                {/* Label row */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 0.5,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                bgcolor: colorConfig.fill,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 500,
                                                color: '#334155',
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                color: colorConfig.fill,
                                                fontWeight: 500,
                                            }}
                                        >
                                            ({colorConfig.label})
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: colorConfig.fill,
                                        }}
                                    >
                                        {item.count} ({percentage.toFixed(0)}%)
                                    </Typography>
                                </Box>

                                {/* Bar */}
                                <Box
                                    sx={{
                                        height: '28px',
                                        bgcolor: colorConfig.bg,
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${barWidth}%`,
                                            height: '100%',
                                            bgcolor: colorConfig.fill,
                                            borderRadius: '6px',
                                            transition: 'width 0.5s ease',
                                        }}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Legend */}
            {total > 0 && (
                <Box
                    sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'center',
                    }}
                >
                    {Object.entries(BUCKET_COLORS).map(([key, config]) => (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                                sx={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    bgcolor: config.fill,
                                }}
                            />
                            <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                {config.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default ResponseTimeChart;

ResponseTimeChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            bucket: PropTypes.string,
            label: PropTypes.string,
            count: PropTypes.number,
        })
    ),
};

ResponseTimeChart.defaultProps = {
    data: [],
};
